import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { COMPANY, OPENING_HOURS } from "@/lib/company";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";

const TITLE = "Contact — Elvion Group Nederland";
const DESCRIPTION =
  "Neem contact op met Elvion Group Nederland: telefoon 085 080 66 95, info@elvion-group.nl, Betsy van Goorstraat 15, 6702DC Wageningen. KvK 98117475.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${COMPANY.website}/contact` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${COMPANY.website}/contact` }],
    scripts: [breadcrumbJsonLd([{ name: "Contact", path: "/contact" }])],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header variant="shop" />
      <main>
        <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl font-extrabold text-primary">Contact</h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-text-secondary">
            U bereikt ons telefonisch, per e-mail of via het formulier hieronder. Wij reageren op
            werkdagen binnen 1 werkdag. Bestellingen, retouren en garantievragen behandelen wij via
            dezelfde kanalen.
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-extrabold text-primary">Bedrijfsgegevens</h2>
              <dl className="mt-4 space-y-2 text-sm text-text-secondary">
                <div><dt className="inline font-semibold text-foreground">Handelsnaam: </dt><dd className="inline">{COMPANY.name}</dd></div>
                <div><dt className="inline font-semibold text-foreground">Adres: </dt><dd className="inline">{COMPANY.address}, {COMPANY.postal}, {COMPANY.country}</dd></div>
                <div><dt className="inline font-semibold text-foreground">KvK: </dt><dd className="inline">{COMPANY.kvk}</dd></div>
                <div><dt className="inline font-semibold text-foreground">Btw-nummer: </dt><dd className="inline">{COMPANY.btw}</dd></div>
              </dl>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-extrabold text-primary">Direct contact</h2>
              <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                <li>
                  Telefoon:{" "}
                  <a href={`tel:${COMPANY.phoneHref}`} className="font-semibold text-orange underline-offset-4 hover:underline">
                    {COMPANY.phone}
                  </a>
                </li>
                <li>
                  E-mail:{" "}
                  <a href={`mailto:${COMPANY.email}`} className="font-semibold text-orange underline-offset-4 hover:underline">
                    {COMPANY.email}
                  </a>
                </li>
                <li>Reactietijd: binnen 1 werkdag</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-extrabold text-primary">Openingstijden</h2>
              <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                {OPENING_HOURS.map((o) => (
                  <li key={o.days} className="flex justify-between gap-4">
                    <span>{o.days}</span>
                    <span className="font-medium text-foreground">{o.hours}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
            <Link to="/verzendbeleid" className="text-orange underline-offset-4 hover:underline">Verzending &amp; levering</Link>
            <Link to="/retourbeleid" className="text-orange underline-offset-4 hover:underline">Retourbeleid</Link>
            <Link to="/garantie" className="text-orange underline-offset-4 hover:underline">Garantie</Link>
            <Link to="/betaalmethoden" className="text-orange underline-offset-4 hover:underline">Betaalmethoden</Link>
            <Link to="/voorwaarden" className="text-orange underline-offset-4 hover:underline">Algemene voorwaarden</Link>
            <Link to="/privacy" className="text-orange underline-offset-4 hover:underline">Privacystatement</Link>
            <Link to="/klachten" className="text-orange underline-offset-4 hover:underline">Klachten &amp; geschillen</Link>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
    </div>
  );
}
